import { ICONS } from "@/common/icons.map";
import { AxisType, SearchTargetType } from "@/gql/types";
export enum POPULATION_TYPE {
  ALL = "ALL",
  NONE = "NONE",
  FOLDER = "FOLDER",
  FILE = "FILE",
  GROUP = "GROUP",
  CONTACT = "CONTACT",
  ORGANIZATION = "ORGANIZATION",
  EMPLOYEE = "EMPLOYEE",
  LABEL = "LABEL",
  CONTEXT = "CONTEXT",
  ACCOUNT = "ACCOUNT",
  PRIVATE_EMAIL = "PRIVATE_EMAIL",
  EVENT = "EVENT",
  DEPARTMENT = "DEPARTMENT",
}

export const PopulationTypeHelper = {
  asString(type: POPULATION_TYPE, plural = false): string {
    switch (type) {
      case POPULATION_TYPE.FILE:
        return "File" + (plural ? "s" : "");
      case POPULATION_TYPE.CONTACT:
        return "External contact" + (plural ? "s" : "");
      case POPULATION_TYPE.EMPLOYEE:
        return "Employee" + (plural ? "s" : "");
      case POPULATION_TYPE.FOLDER:
        return "Cloud folder" + (plural ? "s" : "");
      case POPULATION_TYPE.GROUP:
        return "Group" + (plural ? "s" : "");
      case POPULATION_TYPE.LABEL:
        return "Information type" + (plural ? "s" : "");
      case POPULATION_TYPE.CONTEXT:
        return "Information Context" + (plural ? "s" : "");
      case POPULATION_TYPE.ORGANIZATION:
        return "Organization" + (plural ? "s" : "");
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return "Personal contact";
      case POPULATION_TYPE.DEPARTMENT:
        return "Department" + (plural ? "s" : "");
      case POPULATION_TYPE.ACCOUNT:
        return "Account" + (plural ? "s" : "");
      case POPULATION_TYPE.EVENT:
        return "Event" + (plural ? "s" : "");
      default:
        return "";
    }
  },
  keyAsString(type: POPULATION_TYPE): string {
    switch (type) {
      case POPULATION_TYPE.CONTACT:
        return "CONTACT";
      case POPULATION_TYPE.EMPLOYEE:
        return "EMPLOYEE";
      case POPULATION_TYPE.FOLDER:
        return "FOLDER";
      case POPULATION_TYPE.GROUP:
        return "GROUP";
      case POPULATION_TYPE.LABEL:
        return "LABEL";
      case POPULATION_TYPE.CONTEXT:
        return "CONTEXT";
      case POPULATION_TYPE.ORGANIZATION:
        return "ORGANIZATION";
      case POPULATION_TYPE.ACCOUNT:
        return "ACCOUNT";
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return "PRIVATE_EMAIL";
      case POPULATION_TYPE.EVENT:
        return "EVENT";
      case POPULATION_TYPE.NONE:
      default:
        return "NONE";
    }
  },
  fromKeyString(key: string): POPULATION_TYPE {
    switch (key) {
      case "CONTACT":
        return POPULATION_TYPE.CONTACT;
      case "EMPLOYEE":
        return POPULATION_TYPE.EMPLOYEE;
      case "FOLDER":
        return POPULATION_TYPE.FOLDER;
      case "GROUP":
        return POPULATION_TYPE.GROUP;
      case "LABEL":
        return POPULATION_TYPE.LABEL;
      case "CONTEXT":
        return POPULATION_TYPE.CONTEXT;
      case "ORGANIZATION":
        return POPULATION_TYPE.ORGANIZATION;
      case "ACCOUNT":
        return POPULATION_TYPE.ACCOUNT;
      case "PRIVATE_EMAIL":
        return POPULATION_TYPE.PRIVATE_EMAIL;
      case "EVENT":
        return POPULATION_TYPE.EVENT;
      case "NONE":
      default:
        return POPULATION_TYPE.NONE;
    }
  },
  fromSearchTargetType(type: SearchTargetType): POPULATION_TYPE {
    switch (type) {
      case SearchTargetType.Employees:
        return POPULATION_TYPE.EMPLOYEE;
      case SearchTargetType.Files:
        return POPULATION_TYPE.FILE;
      case SearchTargetType.Folders:
        return POPULATION_TYPE.FOLDER;
      case SearchTargetType.OrganizationContacts:
      case SearchTargetType.ExternalContacts:
        return POPULATION_TYPE.CONTACT;
      case SearchTargetType.Organizations:
        return POPULATION_TYPE.ORGANIZATION;
      case SearchTargetType.PersonalAddresses:
      default:
        return POPULATION_TYPE.PRIVATE_EMAIL;
    }
  },
  // without the Account type
  asArray(): POPULATION_TYPE[] {
    return [
      POPULATION_TYPE.CONTACT,
      POPULATION_TYPE.EMPLOYEE,
      POPULATION_TYPE.FOLDER,
      POPULATION_TYPE.FILE,
      POPULATION_TYPE.ORGANIZATION,
      POPULATION_TYPE.LABEL,
      POPULATION_TYPE.CONTEXT,
      POPULATION_TYPE.PRIVATE_EMAIL,
      POPULATION_TYPE.EVENT,
      POPULATION_TYPE.DEPARTMENT,
    ];
  },
  disableTypes(populationType: POPULATION_TYPE): POPULATION_TYPE[] {
    switch (populationType) {
      case POPULATION_TYPE.EMPLOYEE:
        return [POPULATION_TYPE.EMPLOYEE];
      case POPULATION_TYPE.CONTACT:
      case POPULATION_TYPE.ORGANIZATION:
        return [POPULATION_TYPE.CONTACT, POPULATION_TYPE.ORGANIZATION];
      case POPULATION_TYPE.FOLDER:
        return [POPULATION_TYPE.FOLDER];
      case POPULATION_TYPE.LABEL:
        return [POPULATION_TYPE.LABEL];
      default:
        return [];
    }
  },
  displayList(
    arr: POPULATION_TYPE[] = [],
    keepOrder = false
  ): Array<{ id: string; name: string; icon: string }> {
    const map = (type: POPULATION_TYPE) => ({
      id: type,
      name: PopulationTypeHelper.asString(type),
      icon: ICONS.ENTITIES.get(type),
    });
    let res: Array<{ id: string; name: string; icon: string }> = [];
    if (arr.length > 0) {
      res = PopulationTypeHelper.asArray()
        .filter((t) => arr.includes(t))
        .map(map);
    } else {
      res = PopulationTypeHelper.asArray().map(map);
    }
    if (keepOrder) {
      // @ts-ignore
      return arr.map((type) => res.find((m) => m.id === type));
    } else {
      return res;
    }
  },
  asAxisType(p: POPULATION_TYPE, sender = true): AxisType {
    switch (p) {
      case POPULATION_TYPE.CONTACT:
        return AxisType.ExternalReceiver;
      case POPULATION_TYPE.EMPLOYEE:
        return sender ? AxisType.EmployeeSender : AxisType.EmployeeReceiver;
      case POPULATION_TYPE.ORGANIZATION:
        return AxisType.OrganizationReceiver;
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return AxisType.PersonalAddress;
      case POPULATION_TYPE.FOLDER:
        return AxisType.Folder;
      case POPULATION_TYPE.LABEL:
        return AxisType.ContentLabel;
      case POPULATION_TYPE.EVENT:
      case POPULATION_TYPE.FILE:
      default:
        return AxisType.None;
    }
  },
  fromAxisType(type: AxisType): POPULATION_TYPE {
    switch (type) {
      case AxisType.ExternalReceiver:
      case AxisType.ExternalReceiverType:
      case AxisType.ExternalSender:
        return POPULATION_TYPE.CONTACT;
      case AxisType.EmployeeSender:
      case AxisType.EmployeeReceiverType:
      case AxisType.EmployeeSenderType:
      case AxisType.EmployeeReceiver:
      case AxisType.Employee:
        return POPULATION_TYPE.EMPLOYEE;
      case AxisType.OrganizationReceiver:
      case AxisType.OrganizationReceiverType:
      case AxisType.OrganizationSender:
      case AxisType.UntrustedOrganization:
      case AxisType.UntrustedOrganizationType:
        return POPULATION_TYPE.ORGANIZATION;
      case AxisType.PersonalAddress:
      case AxisType.PersonReceiver:
      case AxisType.PersonReceiverType:
      case AxisType.PersonSender:
      case AxisType.PersonSenderType:
        return POPULATION_TYPE.PRIVATE_EMAIL;
      case AxisType.Folder:
      case AxisType.FolderType:
        return POPULATION_TYPE.FOLDER;
      case AxisType.ContentLabel:
      case AxisType.InfoTypeLabel:
        return POPULATION_TYPE.LABEL;
      case AxisType.None:
      default:
        return POPULATION_TYPE.NONE;
    }
  },
  asAxisType2(
    p: POPULATION_TYPE | "EMPLOYEE_SENDER" | "EMPLOYEE_RECEIVER",
    direction: "sender" | "receiver" | "both" = "both"
  ): AxisType {
    switch (p) {
      case POPULATION_TYPE.CONTACT:
        return AxisType.ExternalReceiver;
      case POPULATION_TYPE.EMPLOYEE:
        switch (direction) {
          case "receiver":
            return AxisType.EmployeeReceiver;
          case "sender":
            return AxisType.EmployeeSender;
          case "both":
          default:
            return AxisType.Employee;
        }
      case POPULATION_TYPE.ORGANIZATION:
        return AxisType.OrganizationReceiver;
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return AxisType.PersonalAddress;
      case POPULATION_TYPE.FOLDER:
        return AxisType.Folder;
      case POPULATION_TYPE.LABEL:
        return AxisType.ContentLabel;
      case POPULATION_TYPE.EVENT:
      case POPULATION_TYPE.FILE:
      default:
        return AxisType.None;
    }
  },

  asTypeAxisType(p: POPULATION_TYPE, sender = true): AxisType {
    switch (p) {
      case POPULATION_TYPE.LABEL:
        return AxisType.ContentLabel;
      case POPULATION_TYPE.CONTACT:
        return AxisType.ExternalReceiverType;
      case POPULATION_TYPE.EMPLOYEE:
        return sender
          ? AxisType.EmployeeSenderType
          : AxisType.EmployeeReceiverType;
      case POPULATION_TYPE.ORGANIZATION:
        return AxisType.OrganizationReceiverType;
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return AxisType.PersonalAddressType;
      case POPULATION_TYPE.FOLDER:
        return AxisType.FolderType;
      case POPULATION_TYPE.EVENT:
      case POPULATION_TYPE.FILE:
      default:
        return AxisType.None;
    }
  },

  asSearchTargetType(p: POPULATION_TYPE): SearchTargetType {
    switch (p) {
      case POPULATION_TYPE.CONTACT:
        return SearchTargetType.ExternalContacts;
      case POPULATION_TYPE.EMPLOYEE:
        return SearchTargetType.Employees;
      case POPULATION_TYPE.ORGANIZATION:
        return SearchTargetType.Organizations;
      case POPULATION_TYPE.PRIVATE_EMAIL:
        return SearchTargetType.PersonalAddresses;
      case POPULATION_TYPE.FOLDER:
        return SearchTargetType.Folders;
      case POPULATION_TYPE.FILE:
      default:
        return SearchTargetType.Files;
    }
  },
};
